import { i18n } from '../../../i18n/index.js';
import { formatMoney } from '../../../shared/format/index.js';
import type { DealListItem, DealStage } from '../api/deals.client.js';

export const STAGE_ORDER: DealStage[] = ['New', 'Contacted', 'Negotiation', 'Won', 'Lost'];

export interface KanbanCardModel {
  id: string;
  dealId: string;
  title: string;
  contactName: string;
  amountLabel: string;
  amount?: number | string | null;
  tags: string[];
  stage: DealStage;
  nextActivityDue: string | null;
  lastActivityAt: string | null;
}

export interface KanbanColumnModel {
  stage: DealStage;
  title: string;
  count: number;
  valueLabel: string;
  needsAttentionCount: number;
  cards: KanbanCardModel[];
}

function buildContactName(deal: DealListItem): string {
  const first = deal.contact?.firstName?.trim() ?? '';
  const last = deal.contact?.lastName?.trim() ?? '';
  const full = `${first} ${last}`.trim();
  return full.length > 0
    ? full
    : i18n.t('crm.features.features.deals.lib.kanban.контакт_не_указан');
}

export function moveDealStage(
  deals: DealListItem[],
  dealId: string,
  toStage: DealStage,
): DealListItem[] {
  return deals.map((deal) => {
    if (deal.id !== dealId) {
      return deal;
    }

    return {
      ...deal,
      stage: toStage,
    };
  });
}

export function buildKanbanColumns(deals: DealListItem[]): KanbanColumnModel[] {
  return STAGE_ORDER.map((stage) => {
    const stageDeals = deals.filter((deal) => deal.stage === stage);
    const stageValue = stageDeals.reduce((sum, deal) => {
      const numeric = typeof deal.amount === 'string' ? Number(deal.amount) : deal.amount;
      return sum + (Number.isFinite(numeric) ? numeric : 0);
    }, 0);

    return {
      stage,
      title: stage,
      count: stageDeals.length,
      valueLabel: formatMoney(stageValue, 'RUB'),
      needsAttentionCount: stageDeals.filter(
        (deal) => deal.stage !== 'Won' && deal.stage !== 'Lost' && deal.nextActivityDue === null,
      ).length,
      cards: stageDeals.map((deal) => ({
        id: deal.id,
        dealId: deal.id,
        title: deal.title,
        contactName: buildContactName(deal),
        amountLabel: formatMoney(deal.amount, deal.currency),
        amount: deal.amount,
        tags: deal.tags,
        stage: deal.stage,
        nextActivityDue: deal.nextActivityDue,
        lastActivityAt: deal.lastActivityAt,
      })),
    };
  });
}
